import * as React from "react";
import { useState } from "react";

import * as client from "../../../client";
import { formatAbbreviatedMoneyExact } from "../Money";
import styles from "./financialModelAudit.module.css";

export const fmt = (dollars: number): string =>
  formatAbbreviatedMoneyExact(dollars, true, 2);

const pctOf = (value: number, total: number): string =>
  total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "—";

// The pieces ground-up splits into, in waterfall order. Colors live in the CSS
// module; only the widths (data-driven) are inline. Zero-valued reductions are
// hidden (see GroupFlow), so the rarely-used aggregate rows only show when they
// actually bite.
const STEPS: {
  key: keyof Pick<
    client.FinancialModelAuditGroup,
    | "deductible_savings_aal"
    | "aggregate_deductible_savings_aal"
    | "retained_below_attachment_aal"
    | "limit_savings_aal"
    | "aggregate_limit_savings_aal"
    | "gross_aal"
  >;
  label: string;
  segClass: string;
  reduction: boolean;
}[] = [
  {
    key: "deductible_savings_aal",
    label: "Deductible",
    segClass: styles.segDeductible,
    reduction: true,
  },
  {
    key: "aggregate_deductible_savings_aal",
    label: "Aggregate deductible",
    segClass: styles.segAggDeductible,
    reduction: true,
  },
  {
    key: "retained_below_attachment_aal",
    label: "Retained below attachment",
    segClass: styles.segRetained,
    reduction: true,
  },
  {
    key: "limit_savings_aal",
    label: "Limits",
    segClass: styles.segLimit,
    reduction: true,
  },
  {
    key: "aggregate_limit_savings_aal",
    label: "Aggregate limit",
    segClass: styles.segAggLimit,
    reduction: true,
  },
  {
    key: "gross_aal",
    label: "Gross",
    segClass: styles.segGross,
    reduction: false,
  },
];

export const GroupFlow: React.FC<{
  group: client.FinancialModelAuditGroup;
  // The "all" sentinel zone is never labeled regardless. Callers showing a
  // single whole-peril zone pass false to drop the noise.
  showZoneLabel?: boolean;
}> = ({ group, showZoneLabel = true }) => {
  const groundUp = group.ground_up_aal;
  return (
    <div className={styles.group}>
      {showZoneLabel && group.limit_zone !== "all" && (
        <div className={styles.zoneLabel}>Limit zone: {group.limit_zone}</div>
      )}

      {/* Proportional bar: ground-up = full width, split into its pieces. */}
      <div className={styles.bar}>
        {STEPS.map(({ key, label, segClass }) => {
          const value = group[key];
          if (value <= 0) return null;
          return (
            <div
              key={key}
              className={`${styles.segment} ${segClass}`}
              style={{ width: `${(value / groundUp) * 100}%` }}
              title={`${label}: ${fmt(value)}`}
            />
          );
        })}
      </div>

      {/* Numeric waterfall: ground-up, the non-zero reductions, then gross.
          Zero reductions are dropped so the rarely-used aggregate rows don't
          clutter the common case. */}
      <div className={styles.waterfall}>
        <div className={`${styles.wfRow} ${styles.wfStart}`}>
          <span className={styles.wfLabel}>
            <span className={`${styles.swatch} ${styles.segGroundUp}`} />
            Ground up
          </span>
          <span className={styles.wfAmount}>{fmt(groundUp)}</span>
          <span className={styles.wfPct} />
        </div>
        {STEPS.filter(({ key, reduction }) => !reduction || group[key] > 0).map(
          ({ key, label, segClass, reduction }) => (
            <div
              key={key}
              className={`${styles.wfRow} ${reduction ? styles.wfReduction : styles.wfEnd}`}
            >
              <span className={styles.wfLabel}>
                <span className={`${styles.swatch} ${segClass}`} />
                {reduction ? `− ${label}` : `= ${label}`}
              </span>
              <span className={styles.wfAmount}>{fmt(group[key])}</span>
              <span className={styles.wfPct}>
                {pctOf(group[key], groundUp)}
              </span>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// A peril's deductible and limit terms. With showFunctions, also offers a
// toggle to reveal the resolved deductible/limit function strings.
export const PerilTerms: React.FC<{
  peril: client.FinancialModelAuditPeril;
  showFunctions?: boolean;
}> = ({ peril, showFunctions = false }) => {
  const [showFns, setShowFns] = useState(false);
  const hasFns = peril.deductible_function_str || peril.limit_function_str;
  return (
    <>
      <div className={styles.terms}>
        {peril.deductible_description && (
          <div className={styles.term}>
            <span className={styles.termLabel}>Deductible</span>
            <span>{peril.deductible_description}</span>
          </div>
        )}
        {peril.limit_description && (
          <div className={styles.term}>
            <span className={styles.termLabel}>Limit</span>
            <span>{peril.limit_description}</span>
          </div>
        )}
        {showFunctions && hasFns && (
          <button
            type="button"
            className={styles.fnToggle}
            onClick={() => setShowFns((s) => !s)}
          >
            {showFns ? "Hide functions" : "Show functions"}
          </button>
        )}
      </div>

      {showFunctions && showFns && (
        <div className={styles.fnBlock}>
          {peril.deductible_function_str && (
            <pre className={styles.fnPre}>{peril.deductible_function_str}</pre>
          )}
          {peril.limit_function_str && (
            <pre className={styles.fnPre}>{peril.limit_function_str}</pre>
          )}
        </div>
      )}
    </>
  );
};
